import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Facebook,
  Mail,
  Chrome,
  Music2,
  Sparkles,
  Save,
  Send,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  Copy,
  RefreshCw
} from 'lucide-react';
import { copyToClipboard } from '../../utils/clipboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook/Instagram', icon: Facebook, active: true },
  { id: 'email', name: 'Email', icon: Mail, active: true },
  { id: 'google', name: 'Google Ads', icon: Chrome, active: false },
  { id: 'tiktok', name: 'TikTok', icon: Music2, active: false }
];

const CAMPAIGN_GOALS = [
  { value: 'leads', label: 'Generate Leads', businessFunction: 'Lead Gen', objective: 'lead_generation', funnel: 'TOFU' },
  { value: 'sales', label: 'Drive Sales', businessFunction: 'Sales', objective: 'conversions', funnel: 'BOFU' },
  { value: 'entries', label: 'Membership Entries', businessFunction: 'Membership entries', objective: 'conversions', funnel: 'TOFU' },
  { value: 'awareness', label: 'Brand Awareness', businessFunction: 'Brand', objective: 'brand_awareness', funnel: 'TOFU' },
  { value: 'bookings', label: 'Get Bookings', businessFunction: 'Bookings', objective: 'lead_generation', funnel: 'MOFU' },
  { value: 'calls', label: 'Phone Calls', businessFunction: 'Calls', objective: 'lead_generation', funnel: 'BOFU' }
];

const AWARENESS_LEVELS = [
  { value: 'unaware', label: 'Unaware - Don\'t know they have a problem' },
  { value: 'problem', label: 'Problem Aware - Know the problem, not solutions' },
  { value: 'solution', label: 'Solution Aware - Know solutions exist' },
  { value: 'product', label: 'Product Aware - Know your product' },
  { value: 'most', label: 'Most Aware - Ready to buy' }
];

// Default empty brief — the single source of truth for the form's shape.
const EMPTY_FORM_DATA = {
  // Campaign Brief
  clientName: '',
  launchDate: '',
  campaignName: '',
  campaignGoal: '',
  goalDetails: '',
  offer: '',
  cta: '',
  landingPageUrl: '',
  // Customer Avatar
  ageRange: '',
  location: '',
  whoIsThisPerson: '',
  mainProblem: '',
  whatTheyWant: '',
  interests: '',
  customAudiences: '',
  exclusions: '',
  // Deep Insights - Eugene Schwartz
  awarenessLevel: '',
  triedAndFailed: '',
  exactLanguage: '',
  // Deep Insights - David Ogilvy
  compellingFact: '',
  proofCredentials: '',
  bigPromise: '',
  // Deep Insights - Frank Kern
  beforeState: '',
  afterState: '',
  emotionalPayoff: '',
  objections: '',
  // Must Include
  keyPhrases: '',
  phoneNumber: '',
  deadline: '',
  socialProof: '',
  guarantee: '',
  otherRequirements: '',
  // Budget & Schedule
  dailyBudget: '',
  campaignDuration: '',
  startDate: '',
  endDate: '',
  placements: 'automatic'
};

function CreateCampaign() {
  const navigate = useNavigate();
  const location = useLocation();
  // Pre-fill payload passed via router state (campaign duplicate, offer handoff, ...).
  const prefill = location.state?.prefill || null;

  const [selectedPlatform, setSelectedPlatform] = useState(prefill?.platform || null);
  const [activeTab, setActiveTab] = useState('brief');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [generatedCopy, setGeneratedCopy] = useState(null);
  const [currentAdIndex, setCurrentAdIndex] = useState(prefill?.currentAdIndex || 0);
  const [savedAds, setSavedAds] = useState(prefill?.savedAds || []);
  const [selectedPrimaryText, setSelectedPrimaryText] = useState(null);
  const [selectedHeadline, setSelectedHeadline] = useState(null);

  // Form state — merge any prefill over the empty defaults so every key stays defined.
  const [formData, setFormData] = useState(() => ({
    ...EMPTY_FORM_DATA,
    ...(prefill?.formData || {})
  }));

  // Let the user know the form arrived pre-filled (duplicate / offer handoff).
  // Runs once on mount; `prefill` is derived from immutable router state.
  const prefillNotice = prefill?.notice;
  useEffect(() => {
    if (prefillNotice) {
      toast.success(prefillNotice);
    }
  }, [prefillNotice]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateAvatar = async () => {
    // Validate required brief fields
    if (!formData.clientName || !formData.campaignName || !formData.campaignGoal) {
      toast.error('Please fill in Client Name, Campaign Name, and Goal first');
      return;
    }

    setIsGeneratingAvatar(true);
    try {
      const response = await axios.post(`${API}/campaigns/generate-avatar`, {
        clientName: formData.clientName,
        campaignName: formData.campaignName,
        campaignGoal: formData.campaignGoal,
        goalDetails: formData.goalDetails,
        offer: formData.offer,
        cta: formData.cta,
        landingPageUrl: formData.landingPageUrl
      });

      // Update form with generated avatar data
      const avatarData = response.data;
      setFormData(prev => ({
        ...prev,
        ageRange: avatarData.ageRange || prev.ageRange,
        location: avatarData.location || prev.location,
        whoIsThisPerson: avatarData.whoIsThisPerson || prev.whoIsThisPerson,
        mainProblem: avatarData.mainProblem || prev.mainProblem,
        whatTheyWant: avatarData.whatTheyWant || prev.whatTheyWant,
        interests: avatarData.interests || prev.interests,
        awarenessLevel: avatarData.awarenessLevel || prev.awarenessLevel,
        triedAndFailed: avatarData.triedAndFailed || prev.triedAndFailed,
        exactLanguage: avatarData.exactLanguage || prev.exactLanguage,
        compellingFact: avatarData.compellingFact || prev.compellingFact,
        proofCredentials: avatarData.proofCredentials || prev.proofCredentials,
        bigPromise: avatarData.bigPromise || prev.bigPromise,
        beforeState: avatarData.beforeState || prev.beforeState,
        afterState: avatarData.afterState || prev.afterState,
        emotionalPayoff: avatarData.emotionalPayoff || prev.emotionalPayoff,
        objections: avatarData.objections || prev.objections
      }));

      toast.success('Customer avatar generated! Review and tweak as needed.');
    } catch (error) {
      console.error('Generate avatar error:', error);
      toast.error('Failed to generate avatar. Please fill in manually.');
    } finally {
      setIsGeneratingAvatar(false);
    }
  };

  const generateCampaignName = () => {
    const goal = CAMPAIGN_GOALS.find(g => g.value === formData.campaignGoal);
    if (!goal || !formData.clientName) return '';
    
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `${formData.clientName} | ${goal.businessFunction} | ${goal.objective} | ${goal.funnel} | ${date} | ${formData.campaignName || 'campaign'}`;
  };

  const generateAdSetName = (type = 'cold') => {
    const goal = CAMPAIGN_GOALS.find(g => g.value === formData.campaignGoal);
    if (!goal) return '';
    
    if (type === 'cold') {
      return `${formData.location || 'Location'} | Interest Stack | ${goal.funnel} | All | ${formData.location || 'Location'} | ${formData.ageRange || 'All'} | cold ${formData.campaignGoal}`;
    } else {
      return `${formData.location || 'Location'} | Retargeting | BOFU | All | ${formData.location || 'Location'} | Engaged | ${formData.ageRange || 'All'} | warm audience`;
    }
  };

  const generateAdName = (adNumber) => {
    const date = new Date().toISOString().slice(2, 10).replace(/-/g, '');
    return `${date} | ${formData.clientName || 'Client'} | Ad${adNumber} | V1`;
  };

  const handleGenerateCopy = async () => {
    // Validate required fields
    if (!formData.clientName || !formData.campaignName || !formData.campaignGoal) {
      toast.error('Please fill in Client Name, Campaign Name, and Goal');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/campaigns/generate-copy`, {
        platform: selectedPlatform,
        formData: formData,
        adNumber: currentAdIndex + 1
      });

      setGeneratedCopy(response.data);
      setSelectedPrimaryText(null);
      setSelectedHeadline(null);
      toast.success('AI copy generated successfully!');
    } catch (error) {
      console.error('Generation error:', error);
      toast.error('Failed to generate copy. Using fallback template.');
      
      // Fallback copy generation
      setGeneratedCopy({
        primaryTexts: [
          {
            style: 'Frank Kern',
            text: `🤔 ${formData.mainProblem || 'Struggling with something'}?\n\nLook, I get it. ${formData.beforeState || 'Things aren\'t where you want them to be'}.\n\nBut here's the thing - ${formData.offer || 'we can help'}.\n\n${formData.bigPromise || 'Imagine the transformation'}.\n\n${formData.cta || 'Click to learn more'} 👇`
          },
          {
            style: 'Eugene Schwartz',
            text: `${formData.whatTheyWant || 'Want better results'}?\n\n${formData.compellingFact || 'Here\'s what you need to know'}.\n\n${formData.proofCredentials || 'We\'ve helped hundreds'}.\n\n${formData.offer || 'Special offer available now'}.\n\n${formData.cta || 'Take action today'}.`
          },
          {
            style: 'David Ogilvy',
            text: `FACT: ${formData.compellingFact || `${formData.clientName || 'We'} delivers results`}.\n\nHere's what we offer: ${formData.offer || 'Premium service'}.\n\n${formData.proofCredentials || 'Backed by years of experience and proven results'}.\n\nOur promise: ${formData.bigPromise || 'Transform your results'}.\n\n${formData.cta || 'Contact us'} now to get started.`
          }
        ],
        headlines: [
          { text: formData.offer || 'Special Offer Inside', charCount: (formData.offer || 'Special Offer Inside').length },
          { text: formData.bigPromise || 'Transform Your Results', charCount: (formData.bigPromise || 'Transform Your Results').length },
          { text: `${formData.clientName || 'We'} Can Help You Achieve ${formData.whatTheyWant || 'Your Goals'}`, charCount: `${formData.clientName || 'We'} Can Help You Achieve ${formData.whatTheyWant || 'Your Goals'}`.length }
        ],
        targetingSuggestions: formData.interests || 'Based on your audience targeting'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveAd = () => {
    if (selectedPrimaryText === null || selectedHeadline === null) {
      toast.error('Please select a primary text and headline');
      return;
    }

    const newAd = {
      adName: generateAdName(currentAdIndex + 1),
      primaryText: generatedCopy.primaryTexts[selectedPrimaryText].text,
      primaryTextStyle: generatedCopy.primaryTexts[selectedPrimaryText].style,
      headline: generatedCopy.headlines[selectedHeadline].text,
      adNumber: currentAdIndex + 1
    };

    setSavedAds(prev => [...prev, newAd]);
    toast.success(`Ad ${currentAdIndex + 1} saved!`);

    if (currentAdIndex < 2) {
      // Move to next ad
      setCurrentAdIndex(prev => prev + 1);
      setGeneratedCopy(null);
      setSelectedPrimaryText(null);
      setSelectedHeadline(null);
    }
  };

  const handleSubmitCampaign = async () => {
    if (savedAds.length < 3) {
      toast.error('Please create at least 3 ads before submitting');
      return;
    }

    setIsSaving(true);
    try {
      const campaignData = {
        platform: selectedPlatform,
        status: 'pending',
        ...formData,
        naming: {
          campaign: generateCampaignName(),
          adsetCold: generateAdSetName('cold'),
          adsetRetarget: generateAdSetName('retarget')
        },
        ads: savedAds
      };

      await axios.post(`${API}/campaigns`, campaignData);
      toast.success('Campaign submitted for approval!');
      navigate('/campaigns/queue');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit campaign');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const draftData = {
        platform: selectedPlatform,
        status: 'draft',
        ...formData,
        savedAds,
        currentAdIndex
      };

      await axios.post(`${API}/campaigns/drafts`, draftData);
      toast.success('Draft saved!');
    } catch (error) {
      console.error('Save draft error:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  // Platform Selection Screen
  if (!selectedPlatform) {
    return (
      <div className="p-6 md:p-8 max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Create New Campaign</h1>
          <p className="text-white/60">Select a platform to get started</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {PLATFORMS.map(platform => (
            <Card
              key={platform.id}
              className={`card cursor-pointer transition-all hover:scale-[1.02] ${!platform.active ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => platform.active && setSelectedPlatform(platform.id)}
            >
              <CardContent className="p-6 flex items-center gap-4">
                <div className={`p-3 rounded-lg ${platform.active ? 'bg-[#00d4aa]/20' : 'bg-white/5'}`}>
                  <platform.icon className={`w-8 h-8 ${platform.active ? 'text-[#00d4aa]' : 'text-white/40'}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white">{platform.name}</h3>
                  {!platform.active && (
                    <span className="text-xs text-white/40">Coming Soon</span>
                  )}
                </div>
                {platform.active && <ArrowRight className="w-5 h-5 text-white/40" />}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const progress = (savedAds.length / 3) * 100;

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => setSelectedPlatform(null)}
            className="text-white/60 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {selectedPlatform === 'facebook' ? 'Facebook/Instagram' : 'Email'} Campaign
            </h1>
            <p className="text-sm text-white/60">Fill in the brief and generate AI copy</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleSaveDraft}
          disabled={isSaving}
          className="hidden md:flex"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Draft
        </Button>
      </div>

      {/* Progress Tracker */}
      <Card className="card mb-6">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-mono text-white/60">Campaign Progress</span>
            <span className="text-sm font-mono text-[#00d4aa]">{savedAds.length} of 3 ads created</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#00d4aa] to-[#00e6bb] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-3">
            {[1, 2, 3].map(num => (
              <div
                key={num}
                className={`flex items-center gap-2 ${
                  savedAds.length >= num
                    ? 'text-[#00d4aa]'
                    : currentAdIndex + 1 === num
                    ? 'text-white'
                    : 'text-white/30'
                }`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  savedAds.length >= num
                    ? 'bg-[#00d4aa] text-black'
                    : currentAdIndex + 1 === num
                    ? 'bg-white/20 text-white'
                    : 'bg-white/5 text-white/30'
                }`}>
                  {savedAds.length >= num ? <Check className="w-3 h-3" /> : num}
                </div>
                <span className="text-sm hidden sm:inline">Ad {num}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 mb-6 bg-white/5">
          <TabsTrigger value="brief" className="data-[state=active]:bg-[#00d4aa] data-[state=active]:text-black">
            Creative & Targeting
          </TabsTrigger>
          <TabsTrigger value="budget" className="data-[state=active]:bg-[#00d4aa] data-[state=active]:text-black">
            Budget & Schedule
          </TabsTrigger>
          <TabsTrigger value="generate" className="data-[state=active]:bg-[#00d4aa] data-[state=active]:text-black">
            Generate Copy
          </TabsTrigger>
        </TabsList>

        {/* Brief Tab */}
        <TabsContent value="brief" className="space-y-6">
          {/* Campaign Brief Section */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-white">Campaign Brief</CardTitle>
              <CardDescription className="text-white/60">Basic campaign information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Client Name *</Label>
                  <Input
                    value={formData.clientName}
                    onChange={(e) => updateFormData('clientName', e.target.value)}
                    placeholder="TradesPro"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Launch Date *</Label>
                  <Input
                    type="date"
                    value={formData.launchDate}
                    onChange={(e) => updateFormData('launchDate', e.target.value)}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Campaign Name *</Label>
                  <Input
                    value={formData.campaignName}
                    onChange={(e) => updateFormData('campaignName', e.target.value)}
                    placeholder="Kitchen Renovation Leads"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Campaign Goal *</Label>
                  <Select value={formData.campaignGoal} onValueChange={(v) => updateFormData('campaignGoal', v)}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select goal" />
                    </SelectTrigger>
                    <SelectContent>
                      {CAMPAIGN_GOALS.map(goal => (
                        <SelectItem key={goal.value} value={goal.value}>{goal.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-white/80">Goal Details</Label>
                <Textarea
                  value={formData.goalDetails}
                  onChange={(e) => updateFormData('goalDetails', e.target.value)}
                  placeholder="Describe what success looks like..."
                  className="bg-white/5 border-white/10 text-white mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label className="text-white/80">Offer / Hook</Label>
                <Input
                  value={formData.offer}
                  onChange={(e) => updateFormData('offer', e.target.value)}
                  placeholder="Free consultation valued at $500"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Call to Action</Label>
                  <Input
                    value={formData.cta}
                    onChange={(e) => updateFormData('cta', e.target.value)}
                    placeholder="Book Your Free Consult"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Landing Page URL</Label>
                  <Input
                    value={formData.landingPageUrl}
                    onChange={(e) => updateFormData('landingPageUrl', e.target.value)}
                    placeholder="https://..."
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Avatar Section */}
          <Card className="card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white">Customer Avatar & Targeting</CardTitle>
                  <CardDescription className="text-white/60">Who are we targeting?</CardDescription>
                </div>
                <Button
                  onClick={handleGenerateAvatar}
                  disabled={isGeneratingAvatar || !formData.clientName || !formData.campaignName || !formData.campaignGoal}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {isGeneratingAvatar ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Auto-fill with AI
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Age Range</Label>
                  <Input
                    value={formData.ageRange}
                    onChange={(e) => updateFormData('ageRange', e.target.value)}
                    placeholder="35-55"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => updateFormData('location', e.target.value)}
                    placeholder="Perth Metro"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white/80">Who is this person?</Label>
                <Textarea
                  value={formData.whoIsThisPerson}
                  onChange={(e) => updateFormData('whoIsThisPerson', e.target.value)}
                  placeholder="Homeowner, married, kids have moved out..."
                  className="bg-white/5 border-white/10 text-white mt-1"
                  rows={2}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Their Main Problem</Label>
                  <Textarea
                    value={formData.mainProblem}
                    onChange={(e) => updateFormData('mainProblem', e.target.value)}
                    placeholder="Kitchen falling apart, need reliable tradie"
                    className="bg-white/5 border-white/10 text-white mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label className="text-white/80">What They Want</Label>
                  <Textarea
                    value={formData.whatTheyWant}
                    onChange={(e) => updateFormData('whatTheyWant', e.target.value)}
                    placeholder="Beautiful new kitchen without the stress"
                    className="bg-white/5 border-white/10 text-white mt-1"
                    rows={2}
                  />
                </div>
              </div>
              <div>
                <Label className="text-white/80">Interests & Behaviours</Label>
                <Textarea
                  value={formData.interests}
                  onChange={(e) => updateFormData('interests', e.target.value)}
                  placeholder="Home improvement, interior design, real estate..."
                  className="bg-white/5 border-white/10 text-white mt-1"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Deep Customer Insights */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-white">Deep Customer Insights</CardTitle>
              <CardDescription className="text-white/60">Based on direct response copywriting principles</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Eugene Schwartz */}
              <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <h4 className="text-purple-400 font-semibold mb-3">EUGENE SCHWARTZ</h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-white/80">Awareness Level</Label>
                    <Select value={formData.awarenessLevel} onValueChange={(v) => updateFormData('awarenessLevel', v)}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                        <SelectValue placeholder="Select awareness level" />
                      </SelectTrigger>
                      <SelectContent>
                        {AWARENESS_LEVELS.map(level => (
                          <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/80">What have they tried that failed?</Label>
                    <Textarea
                      value={formData.triedAndFailed}
                      onChange={(e) => updateFormData('triedAndFailed', e.target.value)}
                      placeholder="DIY, cheap contractors..."
                      className="bg-white/5 border-white/10 text-white mt-1"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Their exact language/phrases</Label>
                    <Textarea
                      value={formData.exactLanguage}
                      onChange={(e) => updateFormData('exactLanguage', e.target.value)}
                      placeholder="&quot;Sick of dodgy tradies&quot;, &quot;just want someone reliable&quot;..."
                      className="bg-white/5 border-white/10 text-white mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* David Ogilvy */}
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <h4 className="text-blue-400 font-semibold mb-3">DAVID OGILVY</h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-white/80">Most compelling fact about this offer</Label>
                    <Input
                      value={formData.compellingFact}
                      onChange={(e) => updateFormData('compellingFact', e.target.value)}
                      placeholder="500+ kitchens completed in Perth"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Why should they believe us?</Label>
                    <Textarea
                      value={formData.proofCredentials}
                      onChange={(e) => updateFormData('proofCredentials', e.target.value)}
                      placeholder="20 years experience, 5-star reviews..."
                      className="bg-white/5 border-white/10 text-white mt-1"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">What&apos;s the BIG promise?</Label>
                    <Input
                      value={formData.bigPromise}
                      onChange={(e) => updateFormData('bigPromise', e.target.value)}
                      placeholder="Your dream kitchen in 4 weeks or less"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Frank Kern */}
              <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
                <h4 className="text-orange-400 font-semibold mb-3">FRANK KERN</h4>
                <div className="space-y-3">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/80">BEFORE state (life now)</Label>
                      <Textarea
                        value={formData.beforeState}
                        onChange={(e) => updateFormData('beforeState', e.target.value)}
                        placeholder="Embarrassed by old kitchen..."
                        className="bg-white/5 border-white/10 text-white mt-1"
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label className="text-white/80">AFTER state (with solution)</Label>
                      <Textarea
                        value={formData.afterState}
                        onChange={(e) => updateFormData('afterState', e.target.value)}
                        placeholder="Proud to host dinner parties..."
                        className="bg-white/5 border-white/10 text-white mt-1"
                        rows={2}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/80">Emotional payoff</Label>
                    <Input
                      value={formData.emotionalPayoff}
                      onChange={(e) => updateFormData('emotionalPayoff', e.target.value)}
                      placeholder="Pride, confidence, peace of mind"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Objections that will kill the sale</Label>
                    <Textarea
                      value={formData.objections}
                      onChange={(e) => updateFormData('objections', e.target.value)}
                      placeholder="Too expensive, takes too long, messy..."
                      className="bg-white/5 border-white/10 text-white mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Must Include Section */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-white">Must Include</CardTitle>
              <CardDescription className="text-white/60">Required elements for the copy</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-white/80">Key phrases/words (one per line)</Label>
                <Textarea
                  value={formData.keyPhrases}
                  onChange={(e) => updateFormData('keyPhrases', e.target.value)}
                  placeholder="Free consultation&#10;No obligation&#10;Limited spots"
                  className="bg-white/5 border-white/10 text-white mt-1"
                  rows={3}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Phone Number</Label>
                  <Input
                    value={formData.phoneNumber}
                    onChange={(e) => updateFormData('phoneNumber', e.target.value)}
                    placeholder="08 9123 4567"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Deadline/Urgency</Label>
                  <Input
                    value={formData.deadline}
                    onChange={(e) => updateFormData('deadline', e.target.value)}
                    placeholder="Offer ends Friday"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white/80">Social proof/stats</Label>
                <Input
                  value={formData.socialProof}
                  onChange={(e) => updateFormData('socialProof', e.target.value)}
                  placeholder="500+ happy customers, 4.9 star rating"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/80">Guarantee/Risk reversal</Label>
                <Input
                  value={formData.guarantee}
                  onChange={(e) => updateFormData('guarantee', e.target.value)}
                  placeholder="100% satisfaction guarantee"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              onClick={() => setActiveTab('budget')}
              className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
            >
              Next: Budget & Schedule
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget" className="space-y-6">
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-white">Budget & Schedule</CardTitle>
              <CardDescription className="text-white/60">Campaign spending and timeline</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Daily Budget ($)</Label>
                  <Input
                    type="number"
                    value={formData.dailyBudget}
                    onChange={(e) => updateFormData('dailyBudget', e.target.value)}
                    placeholder="50"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Campaign Duration (days)</Label>
                  <Input
                    type="number"
                    value={formData.campaignDuration}
                    onChange={(e) => updateFormData('campaignDuration', e.target.value)}
                    placeholder="14"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Start Date</Label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => updateFormData('startDate', e.target.value)}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/80">End Date</Label>
                  <Input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => updateFormData('endDate', e.target.value)}
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white/80">Placements</Label>
                <Select value={formData.placements} onValueChange={(v) => updateFormData('placements', v)}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue placeholder="Select placements" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="automatic">Automatic Placements (Recommended)</SelectItem>
                    <SelectItem value="feed">Feed Only</SelectItem>
                    <SelectItem value="stories">Stories Only</SelectItem>
                    <SelectItem value="reels">Reels Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Naming Conventions Preview */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="text-white">Naming Conventions (Auto-generated)</CardTitle>
              <CardDescription className="text-white/60">Based on your inputs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs font-mono text-white/40 mb-1">CAMPAIGN NAME</p>
                <p className="text-sm text-white font-mono break-all">{generateCampaignName() || 'Fill in brief to generate'}</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs font-mono text-white/40 mb-1">AD SET (COLD)</p>
                <p className="text-sm text-white font-mono break-all">{generateAdSetName('cold') || 'Fill in brief to generate'}</p>
              </div>
              <div className="p-3 bg-white/5 rounded-lg">
                <p className="text-xs font-mono text-white/40 mb-1">AD SET (RETARGETING)</p>
                <p className="text-sm text-white font-mono break-all">{generateAdSetName('retarget') || 'Fill in brief to generate'}</p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setActiveTab('brief')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={() => setActiveTab('generate')}
              className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
            >
              Next: Generate Copy
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </TabsContent>

        {/* Generate Copy Tab */}
        <TabsContent value="generate" className="space-y-6">
          {/* Current Ad Info */}
          <Card className="card border-[#00d4aa]/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Currently Creating</p>
                  <p className="text-lg font-bold text-white">Ad {currentAdIndex + 1} of 3</p>
                  <p className="text-xs font-mono text-[#00d4aa]">{generateAdName(currentAdIndex + 1)}</p>
                </div>
                <Button
                  onClick={handleGenerateCopy}
                  disabled={isGenerating}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate with AI
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Generated Copy */}
          {generatedCopy && (
            <>
              {/* Primary Texts */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="text-white">Primary Text Options</CardTitle>
                  <CardDescription className="text-white/60">Select one for your ad</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {generatedCopy.primaryTexts.map((option, idx) => (
                    <div
                      key={idx}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedPrimaryText(idx)}
                      onKeyDown={(e) => e.key === 'Enter' && setSelectedPrimaryText(idx)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all select-none ${
                        selectedPrimaryText === idx
                          ? 'border-[#00d4aa] bg-[#00d4aa]/10'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-mono text-white/40">{option.style} Style</span>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(option.text);
                            }}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                          {selectedPrimaryText === idx && (
                            <Check className="w-4 h-4 text-[#00d4aa]" />
                          )}
                        </div>
                      </div>
                      <p className="text-white whitespace-pre-wrap text-sm">{option.text}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Headlines */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="text-white">Headline Options</CardTitle>
                  <CardDescription className="text-white/60">Select one</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {generatedCopy.headlines.map((headline, idx) => (
                    <div
                      key={idx}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedHeadline(idx)}
                      onKeyDown={(e) => e.key === 'Enter' && setSelectedHeadline(idx)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all select-none flex items-center justify-between ${
                        selectedHeadline === idx
                          ? 'border-[#00d4aa] bg-[#00d4aa]/10'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-white/40">H{idx + 1}</span>
                        <span className="text-white">{headline.text}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white/40">{headline.text.length} chars</span>
                        {selectedHeadline === idx && (
                          <Check className="w-4 h-4 text-[#00d4aa]" />
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Targeting Suggestions */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="text-white">Targeting Suggestions</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/80">{generatedCopy.targetingSuggestions}</p>
                </CardContent>
              </Card>

              {/* Selected Summary */}
              {(selectedPrimaryText !== null || selectedHeadline !== null) && (
                <Card className="card border-[#00d4aa]/30">
                  <CardHeader>
                    <CardTitle className="text-[#00d4aa]">Selected for Ad {currentAdIndex + 1}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {selectedPrimaryText !== null && (
                      <div>
                        <p className="text-xs font-mono text-white/40 mb-1">PRIMARY TEXT</p>
                        <p className="text-sm text-white/80">{generatedCopy.primaryTexts[selectedPrimaryText].text.slice(0, 100)}...</p>
                      </div>
                    )}
                    {selectedHeadline !== null && (
                      <div>
                        <p className="text-xs font-mono text-white/40 mb-1">HEADLINE</p>
                        <p className="text-sm text-white/80">{generatedCopy.headlines[selectedHeadline].text}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={handleGenerateCopy}
                  disabled={isGenerating}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
                
                {savedAds.length < 2 ? (
                  <Button
                    onClick={handleSaveAd}
                    disabled={selectedPrimaryText === null || selectedHeadline === null}
                    className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
                  >
                    Save Ad & Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSaveAd}
                    disabled={selectedPrimaryText === null || selectedHeadline === null}
                    className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black"
                  >
                    Save Final Ad
                    <Check className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </>
          )}

          {/* Submit Campaign Button (after 3 ads) */}
          {savedAds.length >= 3 && (
            <Card className="card border-green-500/30 bg-green-500/5">
              <CardContent className="p-6 text-center">
                <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">All Ads Created!</h3>
                <p className="text-white/60 mb-4">Your campaign is ready for submission</p>
                <Button
                  onClick={handleSubmitCampaign}
                  disabled={isSaving}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Approval
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default CreateCampaign;
